import React from 'react';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import ProductCard from './components/productCard';
import { TModelsListingProps } from './types/productModelsListing.types';
import { ProductModelsListingModel } from './models/productModelsListing.model';
import ctaTypes from '../../enums/CTA.enums';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import CTA from '../@shared/CTA';
import Heading from '../@shared/heading';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import { descriptionKeyModelListing, eyebrowKeyModelListing, titleKeyModelListing } from '../../constants/dictionary.constants';
import '../../styles/@shared/product-card-dashboard.scss';
import useShowProductComponentTitle from '../../hooks/useShowProductComponentTitle';
import './styles.scss';

const ProductModelListing = (props: TModelsListingProps): JSX.Element | null => {
  const model = new ProductModelsListingModel(props);
  const apiResponse = isEmptyResponse(props) && props?.fields?.apiData?.items && props?.fields?.apiData?.items?.length > 0;
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const compareLinkInfo = model.compareLinkInfo?.value;
  const helpMeLinkInfo = model.helpMeButton?.value;
  const hasHeaderValue = !!(model?.eyebrow?.value || model?.title?.value || model?.description?.value);
  const dictionaryTerms = [titleKeyModelListing, eyebrowKeyModelListing, descriptionKeyModelListing];
  const showHeader = useShowProductComponentTitle(hasHeaderValue, dictionaryTerms);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <AnalyticsVisibleEvent event="series_models_visible">
      <div className="series-detail-model-listing product-card-dashboard__wrapper product-white-spacer">
        <div className="product-card-dashboard">
          {showHeader && (
            <div className="product-card__header">
              <Heading
                editable={false}
                eyebrow={model.eyebrow}
                title={model.title}
                subtitle={model.description}
                link={{
                  value: {
                    href: compareLinkInfo?.href,
                    text: compareLinkInfo?.text,
                    target: compareLinkInfo?.target,
                  },
                }}
                useDictionaryFallback
                titleDictionaryKey={titleKeyModelListing}
                eyebrowDictionaryKey={eyebrowKeyModelListing}
                descriptionDictionaryKey={descriptionKeyModelListing}
              />
              {helpMeLinkInfo?.href && helpMeLinkInfo?.text && (
                <CTA
                  field={{
                    value: {
                      href: helpMeLinkInfo?.href,
                      text: helpMeLinkInfo?.text,
                      target: helpMeLinkInfo?.target,
                    },
                  }}
                  type={ctaTypes['primaryOrange']}
                />
              )}
            </div>
          )}
          <section className="product-card-dashboard__products">
            {model.items?.map((productCard) => (
              <ProductCard {...productCard} baseUrl={model.baseUrl} key={`${productCard?.id}`} />
            ))}
          </section>
        </div>
      </div>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(ProductModelListing);
